"use client";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type CardKey = "web" | "candle" | "feedback" | "dream" | "road";

const CARDS: CardKey[] = ["web", "candle", "feedback", "dream", "road"];

// Per-card fan transform (rotation / x / y offset), mirrors the reference deck.
const LAYOUT: Record<CardKey, { r: string; x: number; y: number }> = {
  web: { r: "-18deg", x: -92, y: 16 },
  candle: { r: "-8deg", x: -44, y: 2 },
  feedback: { r: "0deg", x: 0, y: -6 },
  dream: { r: "8deg", x: 44, y: 2 },
  road: { r: "18deg", x: 92, y: 16 },
};

// Wider fan on tablet/desktop.
const LAYOUT_WIDE: Partial<Record<CardKey, number>> = {
  web: -132,
  candle: -64,
  dream: 64,
  road: 132,
};

export function CardDeck({ erupting }: { erupting: boolean }) {
  const { t } = useTranslation();
  const [flipped, setFlipped] = useState<Record<string, boolean>>({});
  const [isWide, setIsWide] = useState(false);

  // Track viewport width for the wider fan (matches the reference @760px breakpoint).
  useEffect(() => {
    const mq = window.matchMedia("(min-width:760px)");
    const sync = () => setIsWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  return (
    <div
      className="relative mb-1.5 h-[178px] w-full max-w-[520px] self-end md:h-[190px]"
      style={{ perspective: "900px" }}
      aria-label={t("scene.deckLabel")}
      data-el="card-deck"
    >
      {CARDS.map((key) => {
        const l = LAYOUT[key];
        const x = isWide && LAYOUT_WIDE[key] !== undefined ? LAYOUT_WIDE[key]! : l.x;
        const isFlipped = !!flipped[key];
        return (
          <button
            key={key}
            type="button"
            aria-label={t(`scene.cards.${key}.front`)}
            data-el={`card-${key}`}
            onClick={() =>
              setFlipped((f) => ({ ...f, [key]: !f[key] }))
            }
            className="group absolute bottom-2 left-1/2 grid h-[126px] w-[82px] -ml-[41px] cursor-pointer place-items-center rounded-[18px] text-[#082116] md:h-[146px] md:w-[96px] md:-ml-12"
            style={{
              border: "1px solid rgba(216,255,240,.45)",
              background:
                "linear-gradient(160deg,rgba(245,255,250,.82),rgba(126,216,176,.18)),radial-gradient(circle at 50% 28%,rgba(126,216,176,.55),transparent 42%)",
              boxShadow:
                "0 18px 40px rgba(0,0,0,.45),0 0 22px rgba(126,216,176,.22)",
              transformStyle: "preserve-3d",
              transition:
                "transform .7s cubic-bezier(.16,1,.3,1), filter .4s ease",
              transform: `rotate(${l.r}) translateX(${x}px) translateY(${
                isFlipped ? l.y - 28 : l.y
              }px)${isFlipped ? " rotateY(180deg)" : ""}`,
              filter: isFlipped ? "brightness(1.15)" : undefined,
              animation: erupting
                ? "ng-lift .9s cubic-bezier(.16,1,.3,1)"
                : undefined,
              // Expose CSS vars for the lift keyframe.
              ["--r" as string]: l.r,
              ["--x" as string]: `${x}px`,
              ["--y" as string]: `${isFlipped ? l.y - 28 : l.y}px`,
            }}
          >
            {/* Front (sigil) */}
            <span
              className="absolute inset-2 grid place-items-center rounded-[13px] font-heading text-[28px] text-white/90"
              style={{
                backfaceVisibility: "hidden",
                textShadow: "0 0 16px rgba(126,216,176,.7)",
              }}
            >
              {t(`scene.cards.${key}.sigil`)}
            </span>
            {/* Back (face text) */}
            <span
              className="absolute inset-2 grid place-items-center rounded-[13px] p-2.5 text-center text-[12px] leading-[1.35] text-scene-text"
              style={{
                transform: "rotateY(180deg)",
                backfaceVisibility: "hidden",
                background: "rgba(7,18,14,.72)",
                border: "1px solid rgba(126,216,176,.24)",
              }}
            >
              {t(`scene.cards.${key}.faceTop`)}
              <br />
              {t(`scene.cards.${key}.faceBottom`)}
            </span>
          </button>
        );
      })}
    </div>
  );
}
